<script lang="ts">
  import {createEventDispatcher, onMount} from 'svelte';

  type TimeValue = Record<'hours' | 'minutes' | 'seconds', string>;
  type TimeType = keyof TimeValue;
  type Options = Record<TimeType, {min: number; max: number}>;

  const DEFAULT_OPTIONS: Options = {
    hours: {min: 0, max: 24},
    minutes: {min: 0, max: 60},
    seconds: {min: 0, max: 60}
  };

  export let hours = '00';
  export let minutes = '00';
  export let seconds = '00';
  export let label = 'Time:';
  export let name = label.toLowerCase().replace(/\s+/g, '-');
  export let options = DEFAULT_OPTIONS;

  const dispatch = createEventDispatcher<{input: TimeValue}>();
  let mounted = false;

  const cleanDigits = (value: string) => value.replace(/[^\d]/g, '').slice(0, 2);

  const formatTime = (value: string, max: number): string => {
    const digits = cleanDigits(value);
    if (!digits) return '00';
    const num = parseInt(digits);
    return num <= max ? digits.padStart(2, '0') : '00';
  };

  const updateValue = (type: TimeType, value: string): TimeValue => {
    const formattedValue = formatTime(value, options[type].max);

    switch (type) {
      case 'hours':
        hours = formattedValue;
        break;
      case 'minutes':
        minutes = formattedValue;
        break;
      case 'seconds':
        seconds = formattedValue;
        break;
    }

    dispatch('input', {hours, minutes, seconds});
    return {hours, minutes, seconds};
  };

  const handleInput = (e: Event, type: TimeType): void => {
    const input = e.target as HTMLInputElement;
    const rawValue = cleanDigits(input.value);

    if (rawValue.length <= 1) {
      input.value = rawValue;
      return;
    }

    input.value = updateValue(type, rawValue)[type];
  };

  const handleBlur = (e: Event, type: TimeType): void => {
    const input = e.target as HTMLInputElement;
    input.value = updateValue(type, input.value || '00')[type];
  };

  onMount(() => {
    mounted = true;
    options = {...DEFAULT_OPTIONS, ...options};
  });

  $: if (mounted) {
    updateValue('hours', hours);
    updateValue('minutes', minutes);
    updateValue('seconds', seconds);
  }

  $: timeInputs = [
    ['hours', hours],
    ['minutes', minutes],
    ['seconds', seconds]
  ] as const;
</script>

<div class="flex items-center gap-x-2">
  <label for={name} class="shrink-0 text-xs text-sermon-base-300 dark:text-zinc-200">{label}</label>
  <div id={name} class="flex rounded-lg border border-gray-300 bg-white p-1 dark:border-zinc-600 dark:bg-zinc-800">
    {#each timeInputs as [type, value], i}
      {#if i > 0}
        <span class="flex items-center text-xs text-sermon-base-300 dark:text-zinc-200">:</span>
      {/if}
      <input
        type="text"
        {value}
        on:input={e => handleInput(e, type)}
        on:blur={e => handleBlur(e, type)}
        class="w-[35px] border-0 p-1 text-center text-xs font-medium text-sermon-base-300 focus:outline-none focus:ring-0 dark:bg-zinc-800 dark:text-zinc-200"
        placeholder="00"
        required
      />
    {/each}
  </div>
</div>
